"use client";
import { forwardRef, useCallback } from "react"

// --- Lib ---
import { parseShortcutKeys } from "@/tiptap/lib/tiptap-utils"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

import {
  EMOJI_TRIGGER_SHORTCUT_KEY,
  useEmojiTrigger,
} from "@/tiptap/ui/emoji-trigger-button"

import { Button } from "@/tiptap/ui-primitive/button"
import { Badge } from "@/tiptap/ui-primitive/badge"

export function EmojiTriggerShortcutBadge({
  shortcutKeys = EMOJI_TRIGGER_SHORTCUT_KEY
}) {
  return <Badge>{parseShortcutKeys({ shortcutKeys })}</Badge>;
}

/**
 * Button component for adding emoji trigger in a Tiptap editor.
 *
 * For custom button implementations, use the `useEmojiTrigger` hook instead.
 */
export const EmojiTriggerButton = forwardRef((
  {
    editor: providedEditor,
    node,
    nodePos,
    text,
    trigger = ":",
    hideWhenUnavailable = false,
    onTriggerApplied,
    showShortcut = false,
    onClick,
    children,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const {
    isVisible,
    canAddTrigger,
    handleAddTrigger,
    label,
    shortcutKeys,
    Icon,
  } = useEmojiTrigger({
    editor,
    node,
    nodePos,
    trigger,
    hideWhenUnavailable,
    onTriggerApplied,
  })

  const handleClick = useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    handleAddTrigger()
  }, [handleAddTrigger, onClick])

  if (!isVisible) {
    return null
  }

  return (
    <Button
      type="button"
      variant="ghost"
      role="button"
      tabIndex={-1}
      disabled={!canAddTrigger}
      data-disabled={!canAddTrigger}
      aria-label={label}
      tooltip="Add emoji"
      onClick={handleClick}
      {...buttonProps}
      ref={ref}>
      {children ?? (
        <>
          <Icon className="tiptap-button-icon" />
          {text && <span className="tiptap-button-text">{text}</span>}
          {showShortcut && (
            <EmojiTriggerShortcutBadge shortcutKeys={shortcutKeys} />
          )}
        </>
      )}
    </Button>
  );
})

EmojiTriggerButton.displayName = "EmojiTriggerButton"
